// ===========================================
// Slip Signing Form
// ===========================================
// The per-student consent form, shared by the authenticated portal page and the
// public magic-link page.
//
// ONE component for both surfaces on purpose. The two pages differ only in how
// they authenticate and which mutation they call; duplicating the form would
// mean a consent question could be fixed on one path and not the other.

import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SignaturePad, { SignatureValue } from './SignaturePad';
import { unescapeHtml } from '../../utils/sanitize';
import type {
  PermissionSlipResponse,
  PermissionSlipSummary,
  SignSlipInput,
} from '../../api/permissionSlips.api';

export interface SlipSigningFormProps {
  slip: PermissionSlipSummary;
  responses: PermissionSlipResponse[];
  onSign: (data: SignSlipInput) => Promise<unknown>;
  onDecline: (studentId: string, parentNotes?: string) => Promise<unknown>;
  isSubmitting?: boolean;
}

interface StudentAnswers {
  /** THE decision for this child: true = taking part, false = declined. */
  attending: boolean;
  mediaConsent: boolean;
  medicalConsent: boolean;
  transportConsent: boolean;
  dietaryNotes: string;
  parentNotes: string;
}

const emptyAnswers = (): StudentAnswers => ({
  // Defaults to taking part: the slip was sent because the school expects the child
  // there, so this is the answer the parent is usually confirming. Declining is the
  // deliberate action.
  attending: true,
  mediaConsent: false,
  medicalConsent: false,
  transportConsent: false,
  dietaryNotes: '',
  parentNotes: '',
});

export default function SlipSigningForm({
  slip,
  responses,
  onSign,
  onDecline,
  isSubmitting,
}: SlipSigningFormProps) {
  const pending = useMemo(() => responses.filter((r) => r.status === 'PENDING'), [responses]);
  const completed = useMemo(() => responses.filter((r) => r.status !== 'PENDING'), [responses]);

  const [answers, setAnswers] = useState<Record<string, StudentAnswers>>({});
  const [signature, setSignature] = useState<SignatureValue | null>(null);
  // Owned here, not in SignaturePad, so it can be asked for at the TOP of the form —
  // it gates the submit button at the bottom.
  const [signerName, setSignerName] = useState('');

  const answersFor = (studentId: string): StudentAnswers => answers[studentId] ?? emptyAnswers();

  const setAnswer = (studentId: string, patch: Partial<StudentAnswers>) => {
    setAnswers((prev) => ({
      ...prev,
      [studentId]: { ...answersFor(studentId), ...patch },
    }));
  };

  const buildInput = (studentId: string, override?: StudentAnswers): SignSlipInput => {
    const a = override ?? answersFor(studentId);
    return {
      studentId,
      // Only send answers the slip actually asked for. The server ignores the
      // rest anyway, but sending them would imply a decision nobody made.
      ...(slip.requiresMediaConsent ? { mediaConsent: a.mediaConsent } : {}),
      ...(slip.requiresMedicalConsent ? { medicalConsent: a.medicalConsent } : {}),
      ...(slip.requiresTransportConsent ? { transportConsent: a.transportConsent } : {}),
      ...(slip.requiresDietaryInfo ? { dietaryNotes: a.dietaryNotes || null } : {}),
      parentNotes: a.parentNotes || null,
      signatureMethod: signature!.method,
      signatureSvg: signature!.signatureSvg,
      signatureText: signature!.signatureText,
      signatureFontKey: signature!.signatureFontKey,
      signedByName: signature!.signedByName,
    };
  };

  const attendingIds = pending.filter((r) => answersFor(r.Student.id).attending);
  const decliningIds = pending.filter((r) => !answersFor(r.Student.id).attending);

  /**
   * Submit every pending child, in one action, routing each by their own toggle.
   *
   * There is ONE signature for the whole form, so there is ONE button, at the bottom, after
   * the signature. The previous layout put a "Give permission" button inside each child's
   * card — above the signature they all depended on — so the parent had to tick a child,
   * scroll down to sign, then scroll back up to press. Per-child buttons only make sense if
   * you sign per child, and you don't.
   *
   * The per-child toggle replaced a separate "Decline" button for the same reason: the toggle
   * already IS the decision, so a second control meaning the opposite was redundant and
   * invited contradictory input.
   *
   * Declines go to a different endpoint and need no signature, so they are sent even when the
   * whole form is a decline — which is why the button stays enabled with no signature in that
   * case.
   */
  const handleSubmitAll = async () => {
    // Snapshot the ids: each successful write invalidates the detail query
    // (staleTime 0) and refetches, so `pending` changes underneath the loop.
    const attending = attendingIds.map((r) => r.Student.id);
    const declining = decliningIds.map((r) => r.Student.id);

    // Sequential, each attempt caught individually. Every response is an independent legal
    // record; without the catch, one failure (e.g. a 409 on a child already answered by the
    // refetch) would abort the loop and leave the rest unanswered behind one generic toast.
    // A PARTIAL failure stays visible in the list, which shows who is still pending.
    for (const studentId of declining) {
      try {
        await onDecline(studentId, answersFor(studentId).parentNotes || undefined);
      } catch {
        // Mutation's own error toast already fired; keep going.
      }
    }

    // A signature is required to GRANT permission, never to decline one.
    if (!signature) return;
    for (const studentId of attending) {
      try {
        await onSign(buildInput(studentId));
      } catch {
        // As above.
      }
    }
  };

  // A decline needs no signature. Only require one when at least one child is being
  // granted permission — otherwise a parent declining for everyone would be stuck
  // signing for a decision that records no signature.
  const canSubmit = !isSubmitting && (attendingIds.length === 0 || !!signature);

  /** Names the decision on the button, so it is read BEFORE the press, not discovered after. */
  const submitLabel = (() => {
    const one = pending.length === 1;
    if (attendingIds.length === 0) {
      return one
        ? `Decline for ${pending[0].Student.firstName}`
        : `Decline for all ${pending.length} children`;
    }
    if (decliningIds.length === 0) {
      return one
        ? `Give permission for ${pending[0].Student.firstName}`
        : `Give permission for all ${pending.length} children`;
    }
    // Mixed: say exactly what is going in each direction.
    return `Give permission for ${attendingIds.length}, decline for ${decliningIds.length}`;
  })();

  return (
    <Stack spacing={3}>
      {/* --- What is being consented to --- */}
      <Card elevation={0} sx={{ bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {slip.title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            component="div"
            sx={{ whiteSpace: 'pre-wrap' }}
          >
            {/*
              Stored ESCAPED, so it must be decoded before display or the
              parent reads `&amp;` and `&#x2F;` on the document they are being
              asked to consent to. Rendered as a React text child, so the
              decoded value is re-escaped by React — safe, not dangerouslySet.
            */}
            {unescapeHtml(slip.body)}
          </Typography>
        </CardContent>
      </Card>

      {/* --- Already answered --- */}
      {completed.length > 0 && (
        <Stack spacing={1}>
          {completed.map((r) => (
            <Alert
              key={r.id}
              severity={r.status === 'SIGNED' ? 'success' : 'info'}
              icon={r.status === 'SIGNED' ? <CheckCircleIcon /> : <CancelIcon />}
            >
              {r.Student.firstName} {r.Student.lastName} —{' '}
              {r.status === 'SIGNED' ? 'permission given' : 'declined'}
              {r.signedByName ? ` by ${r.signedByName}` : ''}.
              {' '}A signed response cannot be changed here — contact the school if it is wrong.
            </Alert>
          ))}
        </Stack>
      )}

      {pending.length === 0 ? (
        <Alert severity="success">
          Every child on this permission slip has been answered. Nothing further is needed.
        </Alert>
      ) : (
        <>
          {/*
            --- Who is signing (FIRST) ---
            Deliberately the first thing asked, above the per-child sections.

            This name gates the whole form: `SignaturePad`'s `emit()` treats it as its first
            condition, so while it is empty EVERY "Give permission" button stays disabled no
            matter what else is filled in. It used to render inside the signature block at the
            very bottom, below both children — so a parent worked down the page, ticked the
            consents, found the buttons dead, and the cause was a field they had not scrolled
            to yet. Asking once, up front, makes the rest of the form behave as it looks.
          */}
          {attendingIds.length > 0 && (
            <TextField
              fullWidth
              required
              label="Your full name"
              placeholder="Parent or caregiver name"
              helperText="Your name, the date and time are recorded with your signature."
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              disabled={isSubmitting}
              inputProps={{ maxLength: 200 }}
            />
          )}

          {/* --- Per-student questions --- */}
          {pending.map((r) => {
            const a = answersFor(r.Student.id);
            const showQuestions =
              slip.requiresMediaConsent ||
              slip.requiresMedicalConsent ||
              slip.requiresTransportConsent ||
              slip.requiresDietaryInfo;

            return (
              <Card key={r.id} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {r.Student.firstName} {r.Student.lastName}
                    </Typography>
                    <Chip label="Awaiting response" size="small" color="warning" />
                  </Stack>

                  {/*
                    THE decision for this child, as a single toggle: on = taking part,
                    off = not. It replaces a separate "Decline" button, so the parent makes
                    one choice per child in one place instead of choosing between two
                    controls that meant opposite things.

                    Defaults ON: a permission slip is sent because the school expects the
                    child to attend, so that is the answer the parent is usually confirming.
                    Turning it off greys the child's detail questions — they only describe
                    HOW a child takes part, so they are meaningless once they are not.
                  */}
                  <FormControlLabel
                    sx={{ mb: showQuestions ? 1 : 0 }}
                    control={
                      <Switch
                        checked={a.attending}
                        onChange={(e) => setAnswer(r.Student.id, { attending: e.target.checked })}
                        disabled={isSubmitting}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={500}>
                        {a.attending
                          ? `Yes — ${r.Student.firstName} has permission to take part`
                          : `No — ${r.Student.firstName} will not take part`}
                      </Typography>
                    }
                  />

                  {showQuestions && a.attending && (
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                      {slip.requiresMediaConsent && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={a.mediaConsent}
                              onChange={(e) =>
                                setAnswer(r.Student.id, { mediaConsent: e.target.checked })
                              }
                            />
                          }
                          label="I consent to photos/video of my child being taken and used by the school"
                        />
                      )}
                      {slip.requiresMedicalConsent && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={a.medicalConsent}
                              onChange={(e) =>
                                setAnswer(r.Student.id, { medicalConsent: e.target.checked })
                              }
                            />
                          }
                          label="I consent to staff arranging emergency medical treatment if needed"
                        />
                      )}
                      {slip.requiresTransportConsent && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={a.transportConsent}
                              onChange={(e) =>
                                setAnswer(r.Student.id, { transportConsent: e.target.checked })
                              }
                            />
                          }
                          label="I consent to my child travelling by the transport arranged for this activity"
                        />
                      )}
                      {slip.requiresDietaryInfo && (
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          size="small"
                          label="Allergies, dietary or medical notes"
                          placeholder="Anything staff need to know for this activity"
                          value={a.dietaryNotes}
                          onChange={(e) =>
                            setAnswer(r.Student.id, { dietaryNotes: e.target.value })
                          }
                          sx={{ mt: 1 }}
                          inputProps={{ maxLength: 2000 }}
                        />
                      )}
                    </Stack>
                  )}

                  <TextField
                    fullWidth
                    size="small"
                    label="Anything else for the school (optional)"
                    value={a.parentNotes}
                    onChange={(e) => setAnswer(r.Student.id, { parentNotes: e.target.value })}
                    inputProps={{ maxLength: 2000 }}
                  />

                </CardContent>
              </Card>
            );
          })}

          <Divider />

          {/*
            --- Signature ---
            Hidden when every child has been toggled off: a decline records no signature, so
            asking for one would be demanding a legal mark for a decision that never stores it.
          */}
          {attendingIds.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Signature
              </Typography>
              <SignaturePad
                value={signature}
                onChange={setSignature}
                disabled={isSubmitting}
                signerName={signerName}
              />
            </Box>
          )}

          {/*
            ONE submit, at the end, after the signature it depends on.

            The "use the same answers for all" checkbox is gone with it: it existed only to
            reveal this button, and it was misleading — each child keeps their OWN consent
            answers either way. Only the signature is shared, which is the whole reason a
            single button is correct.

            `signature` is null until BOTH the name and a signature are present, so the hint
            below must cover the whole gap and name the piece that is actually missing.
          */}
          {/* Only nag for a signature when one is actually needed to grant permission. */}
          {attendingIds.length > 0 && !signature && (
            <Alert severity="info">
              {!signerName.trim()
                ? 'Enter your full name at the top of this form, then add your signature.'
                : 'Add your signature above to continue.'}
            </Alert>
          )}

          {/*
            ONE button, and its label states the decision being submitted — so a parent who
            has toggled a child off sees that before they press, not after. A mixed answer
            (some in, some out) is legitimate and is named as such rather than hidden behind
            a generic "Submit".
          */}
          <Button
            variant="contained"
            size="large"
            color={attendingIds.length === 0 ? 'inherit' : 'primary'}
            disabled={!canSubmit}
            onClick={handleSubmitAll}
          >
            {submitLabel}
          </Button>
        </>
      )}
    </Stack>
  );
}
